// Transforma o print que a pessoa anexou em texto, e só o texto segue para a SAK.
//
// 1º tenta a IA (api/ler-print.ts, modelo da OpenAI com visão): entende a tela, descreve o erro
//    e o contexto em poucas frases e deixa de fora dados pessoais.
// 2º se a função não responder (sem chave, fora do ar, demora, `npm start` local sem a função),
//    cai para o OCR no próprio navegador (Tesseract), que pega o texto visível do print.

export type Idioma = "pt" | "es";

export const TIPOS_ACEITOS = ["image/png", "image/jpeg", "image/webp"];
export const TAMANHO_MAXIMO = 10 * 1024 * 1024;
const LIMITE_CARACTERES = 1200;

// Português/espanhol conforme a central, mais inglês: muita mensagem de erro de plataforma
// (Shopify, Nuvemshop, navegador) vem em inglês.
const LINGUAS: Record<Idioma, string[]> = { pt: ["por", "eng"], es: ["spa", "eng"] };

// Dados pessoais que aparecem em prints e não precisam ir para a SAK.
const MASCARAS: [RegExp, string][] = [
  [/[\w.+-]+@[\w-]+\.[\w.-]+/g, "[e-mail]"],
  [/\b\d{3}\.?\d{3}\.?\d{3}-?\d{2}\b/g, "[CPF]"],
  [/\b\d{2}\.?\d{3}\.?\d{3}\/?\d{4}-?\d{2}\b/g, "[CNPJ]"],
  [/\b(?:\d[ -]?){13,19}\b/g, "[número]"],
  [/(?:\+?\d{2}\s?)?\(?\d{2}\)?\s?9?\d{4}[-\s]?\d{4}\b/g, "[telefone]"],
];

export const mascararDadosPessoais = (texto: string) =>
  MASCARAS.reduce((atual, [regex, troca]) => atual.replace(regex, troca), texto);

// OCR devolve linhas soltas e ruído (ícones lidos como "|", "©", "»"): fica só o que parece texto.
const limpar = (texto: string) =>
  texto
    .split("\n")
    .map((linha) => linha.replace(/\s+/g, " ").trim())
    .filter((linha) => (linha.match(/[\p{L}\d]/gu) || []).length >= 3)
    .join("\n")
    .slice(0, LIMITE_CARACTERES)
    .trim();

export const validarImagem = (arquivo: File): "tipo" | "tamanho" | null => {
  if (!TIPOS_ACEITOS.includes(arquivo.type)) return "tipo";
  if (arquivo.size > TAMANHO_MAXIMO) return "tamanho";
  return null;
};

const LADO_MAXIMO_IA = 1600;
const TEMPO_LIMITE_IA_MS = 25000;

// Reduz o print antes de enviar: menos tempo de upload e fica abaixo do limite da Vercel.
const reduzirParaEnvio = async (arquivo: File) => {
  const bitmap = await createImageBitmap(arquivo);
  const escala = Math.min(1, LADO_MAXIMO_IA / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(bitmap.width * escala);
  canvas.height = Math.round(bitmap.height * escala);
  const contexto = canvas.getContext("2d");
  if (!contexto) throw new Error("sem canvas");
  contexto.fillStyle = "#fff"; // PNG transparente vira fundo branco no JPEG
  contexto.fillRect(0, 0, canvas.width, canvas.height);
  contexto.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();
  return canvas.toDataURL("image/jpeg", 0.85);
};

const lerComIa = async (arquivo: File, idioma: Idioma): Promise<string> => {
  const imagem = await reduzirParaEnvio(arquivo);
  const controller = new AbortController();
  const limite = setTimeout(() => controller.abort(), TEMPO_LIMITE_IA_MS);
  try {
    const resposta = await fetch("/api/ler-print", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ imagem, idioma }),
      signal: controller.signal,
    });
    // No `npm start` local não existe a função e o servidor devolve o index.html: vira erro.
    const json = resposta.headers.get("content-type")?.includes("application/json")
      ? await resposta.json()
      : null;
    if (!resposta.ok || typeof json?.texto !== "string") {
      throw new Error(`ler-print: ${resposta.status} ${json?.erro || ""}`);
    }
    return json.texto;
  } finally {
    clearTimeout(limite);
  }
};

const lerComOcr = async (
  arquivo: File,
  idioma: Idioma,
  aoProgredir?: (fracao: number) => void
): Promise<string> => {
  // Import dinâmico: o Tesseract (motor + dados do idioma, ~4 MB na primeira vez, depois em
  // cache) só carrega quando alguém anexa uma imagem.
  const { createWorker } = await import("tesseract.js");
  const worker = await createWorker(LINGUAS[idioma], undefined, {
    logger: (m) => {
      if (m.status === "recognizing text") aoProgredir?.(m.progress);
    },
  });
  try {
    const { data } = await worker.recognize(arquivo);
    return limpar(data.text || "");
  } finally {
    await worker.terminate();
  }
};

export type FonteLeitura = "ia" | "ocr";

export const lerTextoDaImagem = async (
  arquivo: File,
  idioma: Idioma,
  aoProgredir?: (fracao: number, fonte: FonteLeitura) => void
): Promise<{ texto: string; fonte: FonteLeitura }> => {
  try {
    aoProgredir?.(0, "ia");
    const texto = await lerComIa(arquivo, idioma);
    // A IA já omite dados pessoais; a máscara é uma segunda camada.
    return { texto: mascararDadosPessoais(limpar(texto)), fonte: "ia" };
  } catch (erro) {
    console.warn("Leitura por IA indisponível, usando OCR:", erro);
    const texto = await lerComOcr(arquivo, idioma, (fracao) => aoProgredir?.(fracao, "ocr"));
    return { texto: mascararDadosPessoais(texto), fonte: "ocr" };
  }
};
